<template>
  <div
    v-if="visible"
    class="modal"
    style="display: block; width: 100vw"
    :class="{
      'modal-dialog-scrollable': scrollable,
    }"
  >
    <transition appear name="modal" :duration="300">
      <Teleport v-if="visible && modal" to="body">
        <div class="modal-backdrop show"></div>
      </Teleport>
    </transition>

    <slot />
  </div>
</template>

<script lang="ts">
export type UIDialogRootProps = {
  /**
   * Control visibility.
   */
  visible: boolean;
  /**
   * Whether the dialog is a modal.
   * Modal dialogs prevent body scroll and use a backdrop. Default to false.
   */
  modal?: boolean;
  /**
   * Only for modal dialogs. When true, the modal can be dismissed by clicking on the backdrop. Default to false.
   */
  dismissible?: boolean;
  /**
   * Whether the modal content is scrollable. Default to false.
   */
  scrollable?: boolean;
};

export const UIDialogContextKey = "app-dialog";

export type UIDialogContext = {
  dismiss: () => void;
};

export const useUIDialogContext = () =>
  inject<UIDialogContext>(UIDialogContextKey);
</script>

<script setup lang="ts">
import { useScrollLock } from "@vueuse/core";
import { inject, provide, watch } from "vue";

const props = defineProps<UIDialogRootProps>();

type Events = {
  (event: "dismiss"): void;
};
const emit = defineEmits<Events>();
const isBodyScrollLocked = useScrollLock(document);

const dismiss = () => {
  if (props.dismissible) {
    emit("dismiss");
  }
};

provide<UIDialogContext>(UIDialogContextKey, { dismiss });

watch(
  () => props.visible,
  (visible) => {
    isBodyScrollLocked.value = visible;
  },
  { immediate: true }
);
</script>

<style lang="scss" scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 300ms ease-out;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
